// Realti-vs-CosmoLex trust transaction diff, shared by the export modal's
// Resolve Differences screen and the trust statement banner's status check
// (compat bridge). One existing-transactions fetch per matter feeds both —
// stored transaction ids on the Realti items pair the two sides.

import { CosmoLexExistingTransaction, CosmoLexExportItem } from './cosmoLexTypes';

/** The Realti side of one diffable row. */
export interface CosmoLexDiffLine {
    transactionType: 'Deposit' | 'Withdrawal';
    name: string;
    description: string;
    amountCents: number;
    /** Transaction id from a previous export of this row, '' when never sent. */
    linkedTxId: string;
    /** CosmoLex matter that previous export went to. */
    linkedMatterId: string;
}

export type CosmoLexConflictField = 'name' | 'description' | 'amount';

export interface CosmoLexConflict<L extends CosmoLexDiffLine> {
    line: L;
    cosmo: CosmoLexExistingTransaction;
    fields: CosmoLexConflictField[];
}

/**
 * Why a Realti line has nothing live in CosmoLex:
 * - 'never-sent': no stored link.
 * - 'voided': the linked transaction was voided in CosmoLex.
 * - 'deleted': the linked id is gone from the CosmoLex list entirely.
 */
export type CosmoLexNewEntryReason = 'never-sent' | 'voided' | 'deleted';

export interface CosmoLexNewEntry<L extends CosmoLexDiffLine> {
    line: L;
    reason: CosmoLexNewEntryReason;
}

export interface CosmoLexClassification<L extends CosmoLexDiffLine> {
    /** In Realti with nothing live in CosmoLex — can be (re)sent. */
    newEntries: CosmoLexNewEntry<L>[];
    /** Linked on both sides but with differing name/description/amount. */
    conflicts: CosmoLexConflict<L>[];
    /** Live in CosmoLex with no Realti item linking to it — manual Realti update. */
    cosmoOnly: CosmoLexExistingTransaction[];
    /** Linked and matching (cosmo null = linked to a different CosmoLex matter). */
    upToDate: Array<{ line: L; cosmo: CosmoLexExistingTransaction | null }>;
}

/** Case- and whitespace-insensitive text compare — CosmoLex round-trips can pad. */
function textDiffers(a: string, b: string): boolean {
    const norm = (s: string) => s.replace(/\s+/g, ' ').trim().toLowerCase();
    return norm(a) !== norm(b);
}

export function cosmoAmountCents(cosmo: CosmoLexExistingTransaction): number {
    return Math.round(Math.abs(Number(cosmo.amount) || 0) * 100);
}

/**
 * Pair every Realti line with its linked CosmoLex transaction (by stored id)
 * and bucket the result. The CosmoLex list carries every status, so a linked
 * row is inspected rather than inferred: voided rows send the line back to
 * New Entries (flagged as voided), a missing id means the row was deleted in
 * CosmoLex, and Posted/Cleared rows diff like any other — the modal locks
 * their update action. Lines linked to a different matter than the one being
 * compared can't be diffed here — they count as up to date with no CosmoLex
 * row rather than risk a duplicate export.
 */
export function classifyCosmoLexLines<L extends CosmoLexDiffLine>(
    lines: L[],
    existing: CosmoLexExistingTransaction[],
    matterId: string
): CosmoLexClassification<L> {
    const byId = new Map(existing.map((t) => [t.id, t]));
    const linkedIds = new Set(lines.map((l) => l.linkedTxId).filter(Boolean));
    const result: CosmoLexClassification<L> = { newEntries: [], conflicts: [], cosmoOnly: [], upToDate: [] };

    for (const line of lines) {
        if (!line.linkedTxId) {
            result.newEntries.push({ line, reason: 'never-sent' });
            continue;
        }
        if (line.linkedMatterId && line.linkedMatterId !== matterId) {
            result.upToDate.push({ line, cosmo: null });
            continue;
        }
        const cosmo = byId.get(line.linkedTxId);
        if (!cosmo) {
            result.newEntries.push({ line, reason: 'deleted' });
            continue;
        }
        if (cosmo.voided) {
            result.newEntries.push({ line, reason: 'voided' });
            continue;
        }
        const fields: CosmoLexConflictField[] = [];
        if (line.amountCents !== cosmoAmountCents(cosmo)) fields.push('amount');
        // The scrape leaves name empty when the statement carries no payor/payee
        // (the modal falls back to the client name at export time) — an empty
        // Realti side is "no opinion", not a conflict.
        if (line.name && textDiffers(line.name, cosmo.payeeName)) fields.push('name');
        if (line.description && textDiffers(line.description, cosmo.description)) fields.push('description');
        if (fields.length) {
            result.conflicts.push({ line, cosmo, fields });
        } else {
            result.upToDate.push({ line, cosmo });
        }
    }

    // Voided rows (and their reversing adjustments) are not live transactions.
    result.cosmoOnly = existing.filter((t) => !t.voided && !linkedIds.has(t.id));
    return result;
}

/** Map a bridge payload item to a diffable line (mirrors the modal's ExportLine build). */
export function itemToDiffLine(item: CosmoLexExportItem): CosmoLexDiffLine {
    const isDeposit = item.type === 'DEPOSIT';
    const asText = (v: unknown) => (typeof v === 'string' ? v : typeof v === 'number' ? String(v) : '');
    return {
        transactionType: isDeposit ? 'Deposit' : 'Withdrawal',
        name: asText(isDeposit ? item.payee_payor : item.payee_name),
        description: asText(item.description),
        amountCents: Number(item.amountCents) || 0,
        linkedTxId: asText(item.cosmoLexTransactionId),
        linkedMatterId: asText(item.cosmoLexMatterId),
    };
}

/** Banner check: does the trust statement differ from CosmoLex at all? */
export function payloadHasDifferences(
    items: CosmoLexExportItem[],
    existing: CosmoLexExistingTransaction[],
    matterId: string
): boolean {
    const lines = items.filter((i) => (Number(i.amountCents) || 0) > 0).map(itemToDiffLine);
    const c = classifyCosmoLexLines(lines, existing, matterId);
    return c.newEntries.length > 0 || c.conflicts.length > 0 || c.cosmoOnly.length > 0;
}
